import { useEffect, useState } from "react";
import type React from "react";
import {
  FileDropZone,
  FileItem,
  Icon,
  useFileDropZone,
} from "@elaniintc/goes-design-system";
import { getMimeTypes } from "@/pages/digital-files/utils";
interface FileInputProps {
  value: File[] | File | null;
  onChange: (val: File[] | File | null) => void;
  disabled?: boolean;
  acceptedMimeTypes?: string[];
  allowMultiples?: boolean;
  allowed_mimes?: string[];
  maxSizeBytes?: number | null;
}

export const FileInput = ({
  value,
  onChange,
  acceptedMimeTypes,
  disabled = false,
  allowMultiples = false,
  allowed_mimes = [],
  maxSizeBytes,
}: FileInputProps) => {
  const [dragError, setDragError] = useState<string | null>(null);

  const hasFile = Array.isArray(value) ? value.length > 0 : Boolean(value);
  const mimeTypes = acceptedMimeTypes ?? getMimeTypes(allowed_mimes);

  const isMimeAllowed = (mime: string | null | undefined) => {
    if (allowed_mimes?.includes("*")) return true;
    if (!mime) return true;
    return mimeTypes.includes(mime);
  };

  const { setFiles, handleDrop, isDragging, setIsDragging, handleFileChange } =
    useFileDropZone({
      acceptedFileMimeTypes: mimeTypes.includes("*") ? ["*/*"] : mimeTypes,
      acceptedFileExtensions: allowed_mimes,
      maxFileSizeBytes: maxSizeBytes ?? 1024 * 1024 * 1024,
      cb: (files) => {
        setDragError(null);
        if (allowMultiples) {
          onChange(files);
        } else {
          onChange(files[0] || null);
        }
      },
    });

  useEffect(() => {
    if (value) {
      setFiles(Array.isArray(value) ? value : [value]);
    } else {
      setFiles([]);
    }
  }, [value, setFiles]);

  const handleContainerDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const dt = event.dataTransfer;
    if (!dt) return;

    let foundInvalid = false;
    let foundFile = false;

    if (dt.items && dt.items.length > 0) {
      Array.from(dt.items).forEach((item) => {
        if (item.kind === "file") {
          foundFile = true;
          const allowed = isMimeAllowed(item.type);
          if (!allowed) {
            foundInvalid = true;
          }
        }
      });
    } else if (dt.files && dt.files.length > 0) {
      Array.from(dt.files).forEach((file) => {
        foundFile = true;
        const allowed = isMimeAllowed(file.type);
        if (!allowed) {
          foundInvalid = true;
        }
      });
    }

    if (foundFile) {
      if (foundInvalid) {
        setDragError(
          "No puedes arrastrar este tipo de archivo. Verifica las extensiones permitidas.",
        );
      } else {
        setDragError(null);
      }
    }
  };

  const handleContainerDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleContainerDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <section
      className=" inset-0 pt-2 w-full"
      aria-label="Área de subida de archivos"
      onDragEnter={handleContainerDragEnter}
      onDragOver={handleContainerDragOver}
      onDragLeave={handleContainerDragLeave}
    >
      <div className="flex flex-col gap-4">
        {(allowMultiples || !hasFile) && !disabled && (
          <FileDropZone
            isDragging={isDragging}
            onFileDrop={handleDrop}
            setIsDragging={setIsDragging}
            onZoneClick={handleFileChange}
            acceptedFilesMimeTypes={mimeTypes}
            acceptedFilesExtensions={
              allowed_mimes?.includes("*")
                ? ["archivo de cualquier tipo"]
                : allowed_mimes || []
            }
          />
        )}

        <div className="flex flex-col gap-0.5">
          {Array.isArray(value)
            ? value.map((file: File) => (
                <FileItem
                  key={file.name}
                  fileName={file.name}
                  action={
                    !disabled && <Icon type="far" name="trash" size="sm" />
                  }
                  tag={`${(file.size / 1024).toFixed(2)} KB`}
                  onAction={() => {
                    const newFiles = value.filter(
                      (f: File) => f.name !== file.name,
                    );
                    onChange(newFiles);
                    setFiles(newFiles);
                  }}
                  error={
                    maxSizeBytes && file.size > maxSizeBytes
                      ? "Archivo muy pesado, no se incluirá en el proceso."
                      : undefined
                  }
                />
              ))
            : value && (
                <FileItem
                  key={value.name}
                  fileName={value.name}
                  action={
                    !disabled && <Icon type="far" name="trash" size="sm" />
                  }
                  tag={`${(value.size / 1024).toFixed(2)} KB`}
                  onAction={() => {
                    onChange(null);
                    setFiles([]);
                  }}
                  error={
                    maxSizeBytes && value.size > maxSizeBytes
                      ? "Archivo muy pesado, no se incluirá en el proceso."
                      : undefined
                  }
                />
              )}
        </div>
        {dragError && (
          <div className="text-danger flex items-center gap-1.5">
            <span className="text-current text-xs leading-xl font-normal">
              {dragError}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};
